/** zh-CN catalogue `rpe` (W3-09; English: src/formats/rpe/rpe.messages.ts; terms: docs/i18n/glossary.md). */
import type { MessageCatalog } from '../../core/util/types';

const EXPECTED =
  '{expected, select, wholeNumber {整数} number {数字} text {文本} bool {true 或 false} list {列表} object {对象} beat {由三个整数组成的拍数 [a, b, c]} colour {由三个 0 到 255 的整数组成的颜色 [r, g, b]} bezier4 {四个数字 [x1, y1, x2, y2]} xy {两个数字 [x, y]} event {事件对象} note {音符对象} line {判定线对象} layer {层级对象或 null} bpm {BPM 对象} controlPoint {控制点对象} chart {包含 META、BPMList 和 judgeLineList 的对象} eventList {事件列表} noteList {音符列表} controlList {控制点列表} timeTag {包含 name 和 time 的对象} other {其他类型的值}}';
const FOUND =
  '{found, select, null {null} bool {true 或 false} num {数字} str {文本} arr {列表} obj {对象} other {其他类型的值}}';
const ATTACH = 'pause、combonumber、combo、score、bar、name 或 level';

const catalog: MessageCatalog = {
  ns: 'rpe',
  locale: 'zh-CN',
  messages: {
    'report.invalidUtf8': '谱面文件不是有效的 UTF-8 文本。仍已读取；Phira 不会将其识别为 RPE 谱面。',
    'report.bom': '谱面文件以字节顺序标记（BOM）开头。已移除；Phira 无法加载带有它的谱面。',
    'report.leadingWhitespace': '谱面文件以空格或空行开头。已移除；没有它们，Phira 能可靠地识别格式。',
    'report.noMetaSubstring': '谱面没有“META”部分，因此 Phira 不会将其识别为 RPE 谱面。已使用默认的谱面信息。',
    'report.floatInIntField': '“{field}”中的整数带有小数点（例如 1.0）。已取整；Phira 会拒绝这样的谱面。',
    'report.missingRequired': '缺少必需字段“{field}”，已用 RPE 默认值填充。',
    'report.duplicateKey': '字段“{field}”写了两次，已使用最后一个值。Phira 会拒绝这样的谱面。',
    'report.wrongType': '字段“{field}”的值类型错误，已替换为默认值。',
    'report.outOfRange': '“{field}”中的整数超出了此字段允许的范围。已更正；Phira 会拒绝这样的谱面。',
    'report.numberOverflow': '“{field}”中的一个数字太大，无法读取，已被替换。Phira 会拒绝这样的谱面。',
    'report.numberOverflowIgnored': '“{field}”中的一个数字太大，无法存储，已被替换。Phira 不读取此值。',
    'report.negativeUnsigned': '字段“{field}”不能为负数。已更正该值；Phira 会拒绝这样的谱面。',
    'report.tripleZeroDen': '“{field}”中的一个拍数除以零。',
    'report.unknownAttachUI': `UI 绑定 {value} 不是 ${ATTACH} 之一。已转换或移除；Phira 会拒绝这样的谱面。`,
    'report.controlMissingValueKey': '一个距离控制点没有“{field}”值。已将其设为 1；否则 Phira 加载此谱面时会崩溃。',
    'report.controlNonNumericExtra': '一个距离控制点的“{field}”中有非数字的值。已移除；Phira 会拒绝这样的谱面。',
    'report.controlValueClamped':
      '一个距离控制点的“{field}”中的数字对 Phira 来说太大，Phira 会将其读作无穷大。已将其设为 Phira 能容纳的最大数字。',
    'report.valueClamped':
      '“{field}”中的一个数字对 Phira 来说太大，Phira 会将其读作无穷大。已将其设为 Phira 能容纳的最大数字。',
    'report.unpairedSurrogate':
      '“{field}”中的文本包含以 \\u 转义写出的半个字符（未配对的代理项）。已将其替换为字符 U+FFFD；Phira 会拒绝这样的谱面。',
    'report.unpairedSurrogateIgnored':
      '“{field}”中的文本包含以 \\u 转义写出的半个字符（未配对的代理项）。由于无法保存，已将其替换为字符 U+FFFD；Phira 仍能加载谱面。',
    'report.rpeOnlyValue': '仅限 RPE 的字段“{field}”有一个意外的值，已被重置。Phira 不使用它。',
    'report.metaInfoValue':
      '谱面信息“{field}”不是文本，已被清空。Phira 仍能加载谱面；只有在谱面包没有 info.yml 时，它才用此字段填写曲目信息。',
    'report.notKept': '一个 RPE 时间标签有字段“{field}”，此编辑器无法保留它。已移除；Phira 不使用时间标签。',
    'report.rpeVersionReplaced': 'RPE 版本不是整数或数字文本。已替换为 160，即 Phira 对它使用的值。',
    'report.bpmInvalidTime': '一个 BPM 变化的拍数无效（某部分为零、负数或小数），已被移除。',
    'report.bpmInvalid': '已移除速度为 {bpm} 的 BPM 变化：速度必须大于 0。',
    'report.bpmEmpty': '谱面没有有效的 BPM。已使用从第 0 拍开始的 120 BPM；BPM 列表为空时 Phira 会崩溃。',
    'report.bpmUnsorted': 'BPM 变化没有按时间顺序排列。已将其排序；时间不变。',
    'report.bpmUnsortedChanged': 'BPM 变化没有按时间顺序排列。已将其排序，这会使时间与 Phira 中不同。',
    'report.bpmShifted': '第一个 BPM 变化位于第 {beat} 拍。已平移谱面的所有拍数，使其从第 0 拍开始；时间不变。',
    'report.bpmBeatOutOfRange':
      '已移除部分 BPM 变化：将谱面平移到从第 0 拍开始后，它们的拍数超出了谱面能容纳的最大拍数（或需要比拍数能容纳的更细的分数）。',
    'report.eventInvalidTime': '已移除拍数无效（某部分为零、负数或小数）的事件。Phira 对它们会显示错误的值。',
    'report.eventBeatOutOfRange':
      '已移除部分事件：将谱面平移到在第一个 BPM 变化处从第 0 拍开始后，它们的拍数超出了谱面能容纳的最大或最小拍数（或需要比拍数能容纳的更细的分数）。',
    'report.jumpAtRangeEnd':
      '已移除位于谱面能容纳的最后一拍的跳变：那里不能开始任何事件，因为之后没有可以结束的拍数。Phira 从那里起显示跳变后的值。',
    'report.unsortedSame': '事件没有按时间顺序排列。已将其排序；运动在 Phira 中看起来完全相同。',
    'report.unsortedChanged':
      '事件没有按时间顺序排列。已将其排序；Phira 播放它们的方式取决于跳转，因此现在的运动有所不同。',
    'report.speedUnsorted': '流速事件没有按时间顺序排列。已将其排序；Phira 对它们计算出的音符位置不同。',
    'report.speedUnsorted170': '流速事件没有按时间顺序排列。已将其排序，与 Phira 在 RPE 1.7 流速曲线下的做法一致。',
    'report.overlapSliced':
      '已裁剪重叠的事件：每个后面的事件现在都从前一个结束处开始，沿用自己的曲线，与 Phira 的播放方式一致。',
    'report.overlapApprox': '已在 Phira 切换到重叠事件的位置裁剪它们。裁剪后的曲线只能近似匹配。',
    'report.curveNoNumber':
      '已裁剪一个重叠的事件，但其缓动范围太窄，Phira 无法计算其曲线（会除以零），因此在此期间 Phira 不会显示正确的值。已从 Phira 切换到它的位置起保留其自身的缓动；请加宽缓动范围或选择其他缓动。',
    'report.eventsAsJumps': '没有长度、结束早于开始或被另一个事件完全覆盖的事件已变为跳变，与 Phira 的播放方式一致。',
    'report.firstEventZeroLength': '第一个事件没有长度。Phira 在第 {beat} 拍之前显示无效值；现在数值从那里开始。',
    'report.defaultKept':
      '此列表的第一个事件现在从第 0 拍开始，因此 Phira 不会再在谱面最开头显示默认值（白色、空文字、无倾斜或无画笔）。已在第 0 拍之前添加一个带默认值的短事件，使谱面看起来相同。',
    'report.startCut':
      '此列表的第一个事件在第一个 BPM 变化之前开始。它现在从第 0 拍开始，沿自身曲线截断，使 Phira 不会在谱面开头显示默认值。',
    'report.startCutApprox':
      '此列表的第一个事件在第一个 BPM 变化之前开始。它现在从第 0 拍开始；截断后的曲线只能近似匹配。',
    'report.leadInFilled':
      '在第一个事件之前，Phira 会向前延续其曲线。将谱面平移到从第 0 拍开始后，Phira 会改为在那里显示默认值，因此已从第 0 拍起添加一个重现原开头的事件。',
    'report.defaultNotKept':
      '此列表的第一个事件现在从第 0 拍开始，因此 Phira 不再在谱面最开头显示默认值（白色、空文字、无倾斜或无画笔）。该事件位于谱面能容纳的最小拍数，因此其前面放不下带默认值的事件。',
    'report.defaultAtStart':
      '将谱面平移到从第 0 拍开始会改变此列表的最开头：Phira 现在会在那里显示其默认值（白色、空文字、无倾斜或无画笔），而不是原来的值。',
    'report.firstSpeedZeroLength': '第一个流速事件在谱面开头没有长度。Phira 完全无法定位此判定线的音符。',
    'report.speedCompressed': '重叠的流速事件现在从前一个结束处开始，与 Phira 在 RPE 1.7 流速曲线下的做法一致。',
    'report.speedBezier':
      '流速事件带有 Phira 不会使用的自定义曲线。已移除该曲线（流速永远不带自定义曲线，因此切换流速模式不会使 Phira 崩溃）；导入的谱面会将其保存在 phiedit.json 中。',
    'report.speedBezierCrash':
      '流速事件使用了自定义曲线，在 RPE 1.7 流速曲线下这会使 Phira 崩溃。已移除该曲线，因此速度现在跟随缓动类型；导入的谱面会将其保存在 phiedit.json 中。',
    'report.speedBezierShared':
      '流速事件使用了自定义曲线。在 RPE 1.7 流速曲线下，Phira 会从另一个曲线相似的事件中取用该曲线。已移除该曲线，因此速度现在跟随缓动类型，音符位置会不同；导入的谱面会将其保存在 phiedit.json 中。',
    'report.gifUnsorted':
      'GIF 进度事件没有按时间顺序排列。已将其排序；Phira 播放它们的方式取决于其顺序，因此动画可能不同。',
    'report.gifJumpHidden':
      '已移除没有长度或被另一个事件完全覆盖的 GIF 进度事件。下一个事件在同一时刻开始，因此 Phira 从未显示它们。',
    'report.gifJumpDropped':
      '已移除 GIF 进度跳变（第一个位于第 {beat} 拍）。没有长度或被另一个事件完全覆盖的事件会让 Phira 跳到其结束值，并以 GIF 速度继续播放。任何事件都无法在不停止 GIF 的情况下做到这一点，因此 GIF 现在会从之前的进度继续播放。',
    'report.inclineRebuilt': '已重新排序或裁剪音符倾斜事件。Phira 只使用谱面开始时的倾斜。',
    'report.textCounterJump':
      '已添加一个跳变到计数文字（%P%）。Phira 在最后一个事件之后会显示未格式化的此类文字，因此文字可能会短暂地看起来不同。',
    'report.textCounterHold':
      '计数文字（%P%）现在会比在 Phira 中更早地以未格式化形式显示：Phira 会一直显示计数，直到一个已被移除的较晚事件（它没有长度或重复了相同的文字），之后才显示未格式化的文字。',
    'report.emptyFirstLayerAlpha':
      '第一个层级中的空不透明度列表会让 Phira 忽略其他层级，并将判定线显示为完全不透明。它现在是恒定的不透明度 255；被忽略的层级保存在 phiedit.json 中。它们的事件时间和自定义曲线不再影响音符在谱面末尾何时停止移动（max_time），也不再影响 Phira 在事件之间共用的曲线。',
    'report.emptyFirstLayer':
      '第一个层级中的空列表会让 Phira 忽略其他层级上的此属性。已将其移除；被忽略的层级保存在 phiedit.json 中。它们的事件时间和自定义曲线不再影响音符在谱面末尾何时停止移动（max_time），也不再影响 Phira 在事件之间共用的曲线。',
    'report.emptyLaterLayer': '后面层级中的空事件列表会使 Phira 崩溃。已将其移除。',
    'report.emptyTextScale': '空的文字列表变成了带空文字的判定线。这会改变文字的长度：Phira 只在列表为空时将其减半。',
    'report.gifOnNonGif':
      'GIF 进度事件设置在贴图“{texture}”上，但它不是 GIF。Phira 无法加载此谱面。已移除这些事件，并保存在 phiedit.json 中。',
    'report.timeTagInvalid': '已移除一个拍数无效的 RPE 时间标签。Phira 不使用时间标签。',
    'report.timeTagBeatOutOfRange':
      '已移除一个 RPE 时间标签：将谱面平移到从第 0 拍开始后，其拍数超出了谱面能容纳的范围。Phira 不使用时间标签。',
    'report.noteInvalidTime': '已移除拍数无效（某部分为零、负数或小数）的音符。',
    'report.noteBeatOutOfRange':
      '已移除部分音符：将谱面平移到在第一个 BPM 变化处从第 0 拍开始后，它们的拍数超出了谱面能容纳的最大或最小拍数（或需要比拍数能容纳的更细的分数）。',
    'report.unknownNoteType': '一个音符的类型 {type} 未知。已将其变为 Tap；Phira 会拒绝这样的谱面。',
    'report.holdReversed': '一个 Hold 的结束早于开始。现在它在开始处结束。',
    'report.fatherInvalid': '一条判定线指定的父线 {father} 不存在。已移除父线；否则 Phira 会崩溃。',
    'report.fatherCycle': '判定线互相指定为父线，形成了循环。已移除其中一个父线关系；Phira 会拒绝这样的谱面。',
    'kept.leadIn': '第一个事件在第 0 拍之后开始。在它之前 Phira 会向前延续其曲线；已原样保留。',
    'kept.speedBezierLegacy':
      '流速事件带有自定义曲线。Phira 会忽略它们，将流速播放为直线渐变。写回时不带曲线（曲线保存在 phiedit.json 中），因为一旦开启 RPE 1.7 流速曲线，Phira 就会因它们崩溃。',
    'kept.easedSpeedLegacy': '流速事件使用了缓动，但除非开启 RPE 1.7 流速曲线，否则 Phira 会将流速播放为直线渐变。',
    'kept.inclineAnimated': '音符倾斜随时间变化，但 Phira 只使用其在谱面开始时的值。',
    'kept.ignoredList': 'Phira 不会在这种判定线上绘制“{list}”。这些事件为 RPE 保留。',
    'kept.bezierHandles': '自定义曲线的控制柄超出了 0 到 1 的范围。已原样保留。',
    'kept.inertControl': '距离控制“{curve}”有两个点，从 1 开始且为直线，因此 Phira 会完全忽略它。',
    'kept.attachUiNotes': '此判定线绑定到了 UI 元素，因此 Phira 会隐藏其音符（它们仍会被判定）。',
    'kept.attachUiTexture': '此判定线绑定到了 UI 元素，但使用了贴图，这会让 Phira 把该元素画得很小。',
    'kept.zeroLengthHold': '保留了没有长度的 Hold。Phira 会计数并判定它们，并短暂地显示它们。',
    'kept.isFakeRaw': '音符使用了 0 和 1 以外的假键值。Phira 将其视为假键，RPE 则视为真键。',
    'kept.noteColor': '音符带有 RPE 测试版的“color”字段。Phira 会忽略它；为 RPE 保留。',
    'kept.bpmfactor': '此判定线的 BPM 系数为 {value}。RPE 使用它，Phira 不使用，因此两者会不一致。',
    'kept.anchor': '此贴图判定线有自定义锚点。RPE 使用它，Phira 总是将贴图居中。',
    'kept.skewControl': '此判定线有音符斜切控制。RPE 使用它，Phira 不使用。',
    'kept.unknownKeys': '谱面中有此编辑器不认识的字段。已保留它们，并原样写回。',
    'diag.floatInIntField': '{path}：请写不带小数点的整数（{detail}）。',
    'diag.missingRequired': '{path}：此字段是必需的。',
    'diag.wrongType': `{path}：应为${EXPECTED}，实际为${FOUND}。`,
    'diag.wrongTypeValue': `{path}：应为${EXPECTED}，实际为${FOUND}（{detail}）。`,
    'diag.duplicateKey': '{path}：此字段写了两次；Phira 会拒绝。',
    'diag.outOfRange': '{path}：{detail} 超出了此字段允许的整数范围。',
    'diag.numberOverflow': '{path}：{detail} 太大，无法作为数字读取。',
    'diag.otherFragment': '{path}：这一部分在其自己的部分中编辑，在此处会被忽略。',
    'diag.unpairedSurrogate':
      '{path}：文本包含以 \\u 转义写出的半个字符（未配对的代理项）；它无法保存，且 Phira 在读取文本处会拒绝它。',
    'diag.notKept': '{path}：此字段无法保留，会被移除。',
    'diag.negativeUnsigned': '{path}：此值不能为负数（{detail}）。',
    'diag.tripleZeroDen': '{path}：拍数的最后一个数字不能为 0（{detail}）。',
    'diag.bom': '文本以字节顺序标记（BOM）开头。',
    'diag.leadingWhitespace': '文本以空格或空行开头。',
    'diag.invalidUtf8': '文件不是有效的 UTF-8 文本。',
    'diag.noMetaSubstring': '文本中不包含“META”。',
    'diag.unknownAttachUI': `{path}：请使用 ${ATTACH} 之一（{detail}）。`,
    'diag.unknownAttachUIKind': `{path}：请使用 ${ATTACH} 之一（实际为${FOUND}）。`,
    'diag.controlMissingValueKey': '{path}：每个控制点都需要它的值（{detail}）。',
    'diag.controlNonNumericExtra': `{path}：控制点只能包含数字（实际为${FOUND}）。`,
    'diag.controlNonNumericExtraValue': `{path}：控制点只能包含数字（实际为${FOUND}：{detail}）。`,
    'report.bpmDuplicate': '有多个 BPM 变化位于同一拍。只保留了最后一个：Phira 使用它并忽略其他的，因此时间不变。',
    'report.bezierFlag':
      '一个事件以 1 以外的值开启了贝塞尔曲线。Phira 只检查该值是否不为 0，因此已将其写为 1；运动不变。',
  },
};

export default catalog;
